import React, { useState, useRef, useEffect, useMemo } from "react";
import toast from "react-hot-toast";
import Lottie from "lottie-react";
import { landingApis } from "../../../api/landingApi";
import ReCAPTCHA from "react-google-recaptcha";
import "./Demo.css";
import useScrollReveal from "../../../utils/useScrollReveal";
import { Typography, Link, MenuItem, InputAdornment, Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import EmailValidator from "../../validator/EmailValidator";
import CustomInput from "../../common/CustomInput";
import CustomSnackbar from "../../common/CustomSnackbar";
import requestDemoSchema from "../../validator/LandingPage/RequestDemoValidator";

// Animation data embedded directly — no file import needed

const COUNTRY_CODES = [
  { code: "+91", label: "🇮🇳 +91", digits: 10 },
  { code: "+1", label: "🇺🇸 +1", digits: 10 },
  { code: "+44", label: "🇬🇧 +44", digits: 10 },
  { code: "+61", label: "🇦🇺 +61", digits: 9 },
  { code: "+971", label: "🇦🇪 +971", digits: 9 },
  { code: "+65", label: "🇸🇬 +65", digits: 8 },
  { code: "+60", label: "🇲🇾 +60", digits: 9 },
  { code: "+49", label: "🇩🇪 +49", digits: 10 },
  { code: "+33", label: "🇫🇷 +33", digits: 9 },
  { code: "+81", label: "🇯🇵 +81", digits: 10 },
];

const RESEND_COOLDOWN_SECONDS = 30; // industry-standard OTP resend window

const tickAnimationData = {
  v: "5.5.0",
  fr: 30.1369934082031,
  ip: 0,
  op: 121.001102762476,
  w: 1080,
  h: 1080,
  nm: "Comp 1",
  ddd: 0,
  assets: [
    {
      id: "comp_0",
      layers: [
        {
          ddd: 0,
          ind: 1,
          ty: 4,
          nm: "dot/tick mark Outlines 2",
          sr: 1,
          ks: {
            o: {
              a: 1,
              k: [
                {
                  i: { x: [0.667], y: [1] },
                  o: { x: [0.333], y: [0] },
                  t: 60.001,
                  s: [0],
                },
                {
                  i: { x: [0.667], y: [1] },
                  o: { x: [0.333], y: [0] },
                  t: 67.001,
                  s: [100],
                },
                {
                  i: { x: [0.667], y: [1] },
                  o: { x: [0.333], y: [0] },
                  t: 75.001,
                  s: [100],
                },
                { t: 82.0007473266364, s: [0] },
              ],
              ix: 11,
            },
            r: { a: 0, k: 180, ix: 10 },
            p: {
              a: 1,
              k: [
                {
                  i: { x: 0.208, y: 1 },
                  o: { x: 0.133, y: 0 },
                  t: 60.001,
                  s: [540, 540, 0],
                  to: [-43.333, 0, 0],
                  ti: [43.333, 0, 0],
                },
                { t: 82.0007473266364, s: [280, 540, 0] },
              ],
              ix: 2,
            },
            a: { a: 0, k: [540, 540, 0], ix: 1 },
            s: { a: 0, k: [100, 100, 100], ix: 6 },
          },
          ao: 0,
          shapes: [
            {
              ty: "gr",
              it: [
                {
                  ind: 0,
                  ty: "sh",
                  ix: 1,
                  ks: {
                    a: 0,
                    k: {
                      i: [
                        [0, -11.046],
                        [11.046, 0],
                        [0, 11.046],
                        [-11.046, 0],
                      ],
                      o: [
                        [0, 11.046],
                        [-11.046, 0],
                        [0, -11.046],
                        [11.046, 0],
                      ],
                      v: [
                        [20, 0],
                        [0, 20],
                        [-20, 0],
                        [0, -20],
                      ],
                      c: true,
                    },
                    ix: 2,
                  },
                  nm: "Path 1",
                  mn: "ADBE Vector Shape - Group",
                  hd: false,
                },
                {
                  ty: "fl",
                  c: {
                    a: 0,
                    k: [
                      0.15294117647058825, 0.6823529411764706,
                      0.3764705882352941, 1,
                    ],
                    ix: 4,
                  },
                  o: { a: 0, k: 100, ix: 5 },
                  r: 1,
                  bm: 0,
                  nm: "Fill 1",
                  mn: "ADBE Vector Graphic - Fill",
                  hd: false,
                },
                {
                  ty: "tr",
                  p: { a: 0, k: [743.5, 540], ix: 2 },
                  a: { a: 0, k: [0, 0], ix: 1 },
                  s: { a: 0, k: [100, 100], ix: 3 },
                  r: { a: 0, k: 0, ix: 6 },
                  o: { a: 0, k: 100, ix: 7 },
                  sk: { a: 0, k: 0, ix: 4 },
                  sa: { a: 0, k: 0, ix: 5 },
                  nm: "Transform",
                },
              ],
              nm: "Group 1",
              np: 2,
              cix: 2,
              bm: 0,
              ix: 1,
              mn: "ADBE Vector Group",
              hd: false,
            },
          ],
          ip: 0,
          op: 121.001102762476,
          st: 0,
          bm: 0,
        },
        {
          ddd: 0,
          ind: 2,
          ty: 4,
          nm: "dot/tick mark Outlines 3",
          sr: 1,
          ks: {
            o: {
              a: 1,
              k: [
                {
                  i: { x: [0.667], y: [1] },
                  o: { x: [0.333], y: [0] },
                  t: 60.001,
                  s: [0],
                },
                {
                  i: { x: [0.667], y: [1] },
                  o: { x: [0.333], y: [0] },
                  t: 67.001,
                  s: [100],
                },
                {
                  i: { x: [0.667], y: [1] },
                  o: { x: [0.333], y: [0] },
                  t: 75.001,
                  s: [100],
                },
                { t: 82.0007473266364, s: [0] },
              ],
              ix: 11,
            },
            r: { a: 0, k: 0, ix: 10 },
            p: {
              a: 1,
              k: [
                {
                  i: { x: 0.208, y: 1 },
                  o: { x: 0.133, y: 0 },
                  t: 60.001,
                  s: [540, 540, 0],
                  to: [43.333, 0, 0],
                  ti: [-43.333, 0, 0],
                },
                { t: 82.0007473266364, s: [800, 540, 0] },
              ],
              ix: 2,
            },
            a: { a: 0, k: [540, 540, 0], ix: 1 },
            s: { a: 0, k: [100, 100, 100], ix: 6 },
          },
          ao: 0,
          shapes: [
            {
              ty: "gr",
              it: [
                {
                  ind: 0,
                  ty: "sh",
                  ix: 1,
                  ks: {
                    a: 0,
                    k: {
                      i: [
                        [0, -11.046],
                        [11.046, 0],
                        [0, 11.046],
                        [-11.046, 0],
                      ],
                      o: [
                        [0, 11.046],
                        [-11.046, 0],
                        [0, -11.046],
                        [11.046, 0],
                      ],
                      v: [
                        [20, 0],
                        [0, 20],
                        [-20, 0],
                        [0, -20],
                      ],
                      c: true,
                    },
                    ix: 2,
                  },
                  nm: "Path 1",
                  mn: "ADBE Vector Shape - Group",
                  hd: false,
                },
                {
                  ty: "fl",
                  c: {
                    a: 0,
                    k: [0, 0.4549019607843137, 0.9098039215686274, 1],
                    ix: 4,
                  },
                  o: { a: 0, k: 100, ix: 5 },
                  r: 1,
                  bm: 0,
                  nm: "Fill 1",
                  mn: "ADBE Vector Graphic - Fill",
                  hd: false,
                },
                {
                  ty: "tr",
                  p: { a: 0, k: [743.5, 540], ix: 2 },
                  a: { a: 0, k: [0, 0], ix: 1 },
                  s: { a: 0, k: [100, 100], ix: 3 },
                  r: { a: 0, k: 0, ix: 6 },
                  o: { a: 0, k: 100, ix: 7 },
                  sk: { a: 0, k: 0, ix: 4 },
                  sa: { a: 0, k: 0, ix: 5 },
                  nm: "Transform",
                },
              ],
              nm: "Group 1",
              np: 2,
              cix: 2,
              bm: 0,
              ix: 1,
              mn: "ADBE Vector Group",
              hd: false,
            },
          ],
          ip: 0,
          op: 121.001102762476,
          st: 0,
          bm: 0,
        },
        {
          ddd: 0,
          ind: 3,
          ty: 4,
          nm: "dot/tick mark Outlines 4",
          sr: 1,
          ks: {
            o: {
              a: 1,
              k: [
                {
                  i: { x: [0.667], y: [1] },
                  o: { x: [0.333], y: [0] },
                  t: 60.001,
                  s: [0],
                },
                {
                  i: { x: [0.667], y: [1] },
                  o: { x: [0.333], y: [0] },
                  t: 67.001,
                  s: [100],
                },
                {
                  i: { x: [0.667], y: [1] },
                  o: { x: [0.333], y: [0] },
                  t: 75.001,
                  s: [100],
                },
                { t: 82.0007473266364, s: [0] },
              ],
              ix: 11,
            },
            r: { a: 0, k: -90, ix: 10 },
            p: {
              a: 1,
              k: [
                {
                  i: { x: 0.208, y: 1 },
                  o: { x: 0.133, y: 0 },
                  t: 60.001,
                  s: [540, 540, 0],
                  to: [0, -46.667, 0],
                  ti: [0, 46.667, 0],
                },
                { t: 82.0007473266364, s: [540, 260, 0] },
              ],
              ix: 2,
            },
            a: { a: 0, k: [540, 540, 0], ix: 1 },
            s: { a: 0, k: [100, 100, 100], ix: 6 },
          },
          ao: 0,
          shapes: [
            {
              ty: "gr",
              it: [
                {
                  ind: 0,
                  ty: "sh",
                  ix: 1,
                  ks: {
                    a: 0,
                    k: {
                      i: [
                        [0, -11.046],
                        [11.046, 0],
                        [0, 11.046],
                        [-11.046, 0],
                      ],
                      o: [
                        [0, 11.046],
                        [-11.046, 0],
                        [0, -11.046],
                        [11.046, 0],
                      ],
                      v: [
                        [20, 0],
                        [0, 20],
                        [-20, 0],
                        [0, -20],
                      ],
                      c: true,
                    },
                    ix: 2,
                  },
                  nm: "Path 1",
                  mn: "ADBE Vector Shape - Group",
                  hd: false,
                },
                {
                  ty: "fl",
                  c: {
                    a: 0,
                    k: [0.6941176470588235, 0.5647058823529412, 1, 1],
                    ix: 4,
                  },
                  o: { a: 0, k: 100, ix: 5 },
                  r: 1,
                  bm: 0,
                  nm: "Fill 1",
                  mn: "ADBE Vector Graphic - Fill",
                  hd: false,
                },
                {
                  ty: "tr",
                  p: { a: 0, k: [743.5, 540], ix: 2 },
                  a: { a: 0, k: [0, 0], ix: 1 },
                  s: { a: 0, k: [100, 100], ix: 3 },
                  r: { a: 0, k: 0, ix: 6 },
                  o: { a: 0, k: 100, ix: 7 },
                  sk: { a: 0, k: 0, ix: 4 },
                  sa: { a: 0, k: 0, ix: 5 },
                  nm: "Transform",
                },
              ],
              nm: "Group 1",
              np: 2,
              cix: 2,
              bm: 0,
              ix: 1,
              mn: "ADBE Vector Group",
              hd: false,
            },
          ],
          ip: 0,
          op: 121.001102762476,
          st: 0,
          bm: 0,
        },
        {
          ddd: 0,
          ind: 4,
          ty: 4,
          nm: "dot/tick mark Outlines",
          sr: 1,
          ks: {
            o: {
              a: 1,
              k: [
                {
                  i: { x: [0.667], y: [1] },
                  o: { x: [0.333], y: [0] },
                  t: 60.001,
                  s: [0],
                },
                {
                  i: { x: [0.667], y: [1] },
                  o: { x: [0.333], y: [0] },
                  t: 67.001,
                  s: [100],
                },
                {
                  i: { x: [0.667], y: [1] },
                  o: { x: [0.333], y: [0] },
                  t: 75.001,
                  s: [100],
                },
                { t: 82.0007473266364, s: [0] },
              ],
              ix: 11,
            },
            r: { a: 0, k: 0, ix: 10 },
            p: {
              a: 1,
              k: [
                {
                  i: { x: 0.208, y: 1 },
                  o: { x: 0.133, y: 0 },
                  t: 60.001,
                  s: [540, 540, 0],
                  to: [43.333, 0, 0],
                  ti: [-43.333, 0, 0],
                },
                { t: 82.0007473266364, s: [800, 540, 0] },
              ],
              ix: 2,
            },
            a: { a: 0, k: [540, 540, 0], ix: 1 },
            s: { a: 0, k: [100, 100, 100], ix: 6 },
          },
          ao: 0,
          shapes: [
            {
              ty: "gr",
              it: [
                {
                  ind: 0,
                  ty: "sh",
                  ix: 1,
                  ks: {
                    a: 0,
                    k: {
                      i: [
                        [0, -11.046],
                        [11.046, 0],
                        [0, 11.046],
                        [-11.046, 0],
                      ],
                      o: [
                        [0, 11.046],
                        [-11.046, 0],
                        [0, -11.046],
                        [11.046, 0],
                      ],
                      v: [
                        [20, 0],
                        [0, 20],
                        [-20, 0],
                        [0, -20],
                      ],
                      c: true,
                    },
                    ix: 2,
                  },
                  nm: "Path 1",
                  mn: "ADBE Vector Shape - Group",
                  hd: false,
                },
                {
                  ty: "fl",
                  c: {
                    a: 0,
                    k: [0.298039215686, 0.788235294118, 0.941176470588, 1],
                    ix: 4,
                  },
                  o: { a: 0, k: 100, ix: 5 },
                  r: 1,
                  bm: 0,
                  nm: "Fill 1",
                  mn: "ADBE Vector Graphic - Fill",
                  hd: false,
                },
                {
                  ty: "tr",
                  p: { a: 0, k: [743.5, 540], ix: 2 },
                  a: { a: 0, k: [0, 0], ix: 1 },
                  s: { a: 0, k: [100, 100], ix: 3 },
                  r: { a: 0, k: 0, ix: 6 },
                  o: { a: 0, k: 100, ix: 7 },
                  sk: { a: 0, k: 0, ix: 4 },
                  sa: { a: 0, k: 0, ix: 5 },
                  nm: "Transform",
                },
              ],
              nm: "Group 1",
              np: 2,
              cix: 2,
              bm: 0,
              ix: 1,
              mn: "ADBE Vector Group",
              hd: false,
            },
          ],
          ip: 0,
          op: 121.001102762476,
          st: 0,
          bm: 0,
        },
        {
          ddd: 0,
          ind: 5,
          ty: 4,
          nm: "dot/tick mark Outlines 5",
          sr: 1,
          ks: {
            o: {
              a: 1,
              k: [
                {
                  i: { x: [0.667], y: [1] },
                  o: { x: [0.333], y: [0] },
                  t: 60.001,
                  s: [0],
                },
                {
                  i: { x: [0.667], y: [1] },
                  o: { x: [0.333], y: [0] },
                  t: 67.001,
                  s: [100],
                },
                {
                  i: { x: [0.667], y: [1] },
                  o: { x: [0.333], y: [0] },
                  t: 75.001,
                  s: [100],
                },
                { t: 82.0007473266364, s: [0] },
              ],
              ix: 11,
            },
            r: { a: 0, k: 90, ix: 10 },
            p: {
              a: 1,
              k: [
                {
                  i: { x: 0.208, y: 1 },
                  o: { x: 0.133, y: 0 },
                  t: 60.001,
                  s: [540, 540, 0],
                  to: [0, 43.333, 0],
                  ti: [0, -43.333, 0],
                },
                { t: 82.0007473266364, s: [540, 800, 0] },
              ],
              ix: 2,
            },
            a: { a: 0, k: [540, 540, 0], ix: 1 },
            s: { a: 0, k: [100, 100, 100], ix: 6 },
          },
          ao: 0,
          shapes: [
            {
              ty: "gr",
              it: [
                {
                  ind: 0,
                  ty: "sh",
                  ix: 1,
                  ks: {
                    a: 0,
                    k: {
                      i: [
                        [0, -11.046],
                        [11.046, 0],
                        [0, 11.046],
                        [-11.046, 0],
                      ],
                      o: [
                        [0, 11.046],
                        [-11.046, 0],
                        [0, -11.046],
                        [11.046, 0],
                      ],
                      v: [
                        [20, 0],
                        [0, 20],
                        [-20, 0],
                        [0, -20],
                      ],
                      c: true,
                    },
                    ix: 2,
                  },
                  nm: "Path 1",
                  mn: "ADBE Vector Shape - Group",
                  hd: false,
                },
                {
                  ty: "fl",
                  c: {
                    a: 0,
                    k: [
                      0.2823529411764706, 0.8352941176470589,
                      0.7764705882352941, 1,
                    ],
                    ix: 4,
                  },
                  o: { a: 0, k: 100, ix: 5 },
                  r: 1,
                  bm: 0,
                  nm: "Fill 1",
                  mn: "ADBE Vector Graphic - Fill",
                  hd: false,
                },
                {
                  ty: "tr",
                  p: { a: 0, k: [743.5, 540], ix: 2 },
                  a: { a: 0, k: [0, 0], ix: 1 },
                  s: { a: 0, k: [100, 100], ix: 3 },
                  r: { a: 0, k: 0, ix: 6 },
                  o: { a: 0, k: 100, ix: 7 },
                  sk: { a: 0, k: 0, ix: 4 },
                  sa: { a: 0, k: 0, ix: 5 },
                  nm: "Transform",
                },
              ],
              nm: "Group 1",
              np: 2,
              cix: 2,
              bm: 0,
              ix: 1,
              mn: "ADBE Vector Group",
              hd: false,
            },
          ],
          ip: 0,
          op: 121.001102762476,
          st: 0,
          bm: 0,
        },
      ],
    },
  ],
  layers: [
    {
      ddd: 0,
      ind: 1,
      ty: 4,
      nm: "tivk/tick mark Outlines",
      td: 1,
      sr: 1,
      ks: {
        o: { a: 0, k: 100, ix: 11 },
        r: { a: 0, k: 0, ix: 10 },
        p: { a: 0, k: [540, 540, 0], ix: 2 },
        a: { a: 0, k: [540, 540, 0], ix: 1 },
        s: {
          a: 1,
          k: [
            {
              i: { x: [0.833, 0.833, 0.833], y: [0.833, 0.833, 0.833] },
              o: { x: [0.167, 0.167, 0.167], y: [0.167, 0.167, 0.167] },
              t: 0,
              s: [0, 0, 100],
            },
            { t: 60.0005468243681, s: [100, 100, 100] },
          ],
          ix: 6,
        },
      },
      ao: 0,
      shapes: [
        {
          ty: "gr",
          it: [
            {
              ind: 0,
              ty: "sh",
              ix: 1,
              ks: {
                a: 0,
                k: {
                  i: [
                    [0, 0],
                    [0, 0],
                    [-3.275, 3.275],
                    [-3.275, -3.275],
                    [0, 0],
                    [0, 0],
                    [-3.275, -3.276],
                    [3.275, -3.276],
                  ],
                  o: [
                    [0, 0],
                    [-3.275, -3.275],
                    [3.275, -3.275],
                    [0, 0],
                    [0, 0],
                    [3.275, -3.276],
                    [3.276, 3.275],
                    [0, 0],
                  ],
                  v: [
                    [-22.155, 53.346],
                    [-69.401, 6.101],
                    [-69.401, -5.76],
                    [-57.54, -5.76],
                    [-22.155, 29.625],
                    [57.541, -50.07],
                    [69.401, -50.07],
                    [69.401, -38.209],
                  ],
                  c: true,
                },
                ix: 2,
              },
              nm: "Path 1",
              mn: "ADBE Vector Shape - Group",
              hd: false,
            },
            {
              ty: "fl",
              c: {
                a: 0,
                k: [0.999999820485, 0.999999760646, 0.999999820485, 1],
                ix: 4,
              },
              o: { a: 0, k: 100, ix: 5 },
              r: 1,
              bm: 0,
              nm: "Fill 1",
              mn: "ADBE Vector Graphic - Fill",
              hd: false,
            },
            {
              ty: "tr",
              p: { a: 0, k: [532.501, 539.59], ix: 2 },
              a: { a: 0, k: [0, 0], ix: 1 },
              s: { a: 0, k: [100, 100], ix: 3 },
              r: { a: 0, k: 0, ix: 6 },
              o: { a: 0, k: 100, ix: 7 },
              sk: { a: 0, k: 0, ix: 4 },
              sa: { a: 0, k: 0, ix: 5 },
              nm: "Transform",
            },
          ],
          nm: "Group 1",
          np: 2,
          cix: 2,
          bm: 0,
          ix: 1,
          mn: "ADBE Vector Group",
          hd: false,
        },
      ],
      ip: 0,
      op: 121.001102762476,
      st: 0,
      bm: 0,
    },
    {
      ddd: 0,
      ind: 2,
      ty: 4,
      nm: "Shape Layer 1",
      parent: 1,
      tt: 1,
      sr: 1,
      ks: {
        o: { a: 0, k: 100, ix: 11 },
        r: { a: 0, k: 0, ix: 10 },
        p: { a: 0, k: [540, 540, 0], ix: 2 },
        a: { a: 0, k: [0, 0, 0], ix: 1 },
        s: { a: 0, k: [100, 100, 100], ix: 6 },
      },
      ao: 0,
      shapes: [
        {
          ty: "gr",
          it: [
            {
              ind: 0,
              ty: "sh",
              ix: 1,
              ks: {
                a: 0,
                k: {
                  i: [
                    [0, 0],
                    [0, 0],
                    [0, 0],
                  ],
                  o: [
                    [0, 0],
                    [0, 0],
                    [0, 0],
                  ],
                  v: [
                    [-82, -11],
                    [-29, 42],
                    [65.5, -52.5],
                  ],
                  c: false,
                },
                ix: 2,
              },
              nm: "Path 1",
              mn: "ADBE Vector Shape - Group",
              hd: false,
            },
            {
              ty: "st",
              c: { a: 0, k: [1, 1, 1, 1], ix: 3 },
              o: { a: 0, k: 100, ix: 4 },
              w: { a: 0, k: 20, ix: 5 },
              lc: 2,
              lj: 1,
              ml: 4,
              bm: 0,
              nm: "Stroke 1",
              mn: "ADBE Vector Graphic - Stroke",
              hd: false,
            },
            {
              ty: "tr",
              p: { a: 0, k: [0, 0], ix: 2 },
              a: { a: 0, k: [0, 0], ix: 1 },
              s: { a: 0, k: [100, 100], ix: 3 },
              r: { a: 0, k: 0, ix: 6 },
              o: { a: 0, k: 100, ix: 7 },
              sk: { a: 0, k: 0, ix: 4 },
              sa: { a: 0, k: 0, ix: 5 },
              nm: "Transform",
            },
          ],
          nm: "Shape 1",
          np: 3,
          cix: 2,
          bm: 0,
          ix: 1,
          mn: "ADBE Vector Group",
          hd: false,
        },
        {
          ty: "tm",
          s: { a: 0, k: 0, ix: 1 },
          e: {
            a: 1,
            k: [
              {
                i: { x: [0.135], y: [1] },
                o: { x: [0.934], y: [0] },
                t: 30,
                s: [0],
              },
              { t: 60.0005468243681, s: [100] },
            ],
            ix: 2,
          },
          o: { a: 0, k: 0, ix: 3 },
          m: 1,
          ix: 2,
          nm: "Trim Paths 1",
          mn: "ADBE Vector Filter - Trim",
          hd: false,
        },
      ],
      ip: 0,
      op: 121.001102762476,
      st: 0,
      bm: 0,
    },
    {
      ddd: 0,
      ind: 3,
      ty: 4,
      nm: "background/tick mark Outlines",
      sr: 1,
      ks: {
        o: { a: 0, k: 100, ix: 11 },
        r: {
          a: 1,
          k: [
            {
              i: { x: [0.349], y: [1] },
              o: { x: [0.671], y: [0.002] },
              t: 0,
              s: [-180],
            },
            { t: 60.0005468243681, s: [0] },
          ],
          ix: 10,
        },
        p: { a: 0, k: [540, 540, 0], ix: 2 },
        a: { a: 0, k: [540, 540, 0], ix: 1 },
        s: {
          a: 1,
          k: [
            {
              i: { x: [0.349, 0.349, 0.667], y: [1, 1, 1] },
              o: { x: [0.671, 0.671, 0.333], y: [0.004, 0.004, 0] },
              t: 0,
              s: [0, 0, 100],
            },
            { t: 60.0005468243681, s: [100, 100, 100] },
          ],
          ix: 6,
        },
      },
      ao: 0,
      shapes: [
        {
          ty: "gr",
          it: [
            {
              ind: 0,
              ty: "sh",
              ix: 1,
              ks: {
                a: 0,
                k: {
                  i: [
                    [-15.633, -19.34],
                    [0, 0],
                    [-14.921, 4.012],
                    [-1.28, -24.836],
                    [-14.43, -5.524],
                    [13.562, -20.845],
                    [-8.426, -12.951],
                    [23.224, -8.892],
                    [0.795, -15.431],
                    [24.016, 6.458],
                    [9.713, -12.017],
                    [15.634, 19.339],
                    [14.921, -4.011],
                    [1.28, 24.835],
                    [14.429, 5.525],
                    [-13.562, 20.845],
                    [8.427, 12.951],
                    [-23.224, 8.893],
                    [-0.795, 15.43],
                    [-24.015, -6.457],
                    [-9.713, 12.016],
                  ],
                  o: [
                    [0, 0],
                    [9.713, 12.016],
                    [24.016, -6.457],
                    [0.795, 15.43],
                    [23.224, 8.893],
                    [-8.426, 12.951],
                    [13.562, 20.845],
                    [-14.43, 5.525],
                    [-1.28, 24.835],
                    [-14.921, -4.011],
                    [-15.633, 19.339],
                    [-9.713, -12.017],
                    [-24.015, 6.458],
                    [-0.795, -15.431],
                    [-23.224, -8.892],
                    [8.427, -12.951],
                    [-13.562, -20.845],
                    [14.429, -5.524],
                    [1.28, -24.836],
                    [14.921, 4.012],
                    [15.634, -19.34],
                  ],
                  v: [
                    [30.377, -162.422],
                    [30.377, -162.422],
                    [70.894, -149.257],
                    [120.044, -113.547],
                    [145.086, -79.081],
                    [163.859, -21.301],
                    [163.859, 21.302],
                    [145.086, 79.081],
                    [120.044, 113.548],
                    [70.894, 149.257],
                    [30.377, 162.423],
                    [-30.376, 162.423],
                    [-70.895, 149.257],
                    [-120.044, 113.548],
                    [-145.085, 79.081],
                    [-163.859, 21.302],
                    [-163.859, -21.301],
                    [-145.085, -79.081],
                    [-120.044, -113.547],
                    [-70.895, -149.257],
                    [-30.376, -162.422],
                  ],
                  c: true,
                },
                ix: 2,
              },
              nm: "Path 1",
              mn: "ADBE Vector Shape - Group",
              hd: false,
            },
            {
              ty: "fl",
              c: {
                a: 0,
                k: [
                  0.15294117647058825, 0.6823529411764706, 0.3764705882352941,
                  1,
                ],
                ix: 4,
              },
              o: { a: 0, k: 100, ix: 5 },
              r: 1,
              bm: 0,
              nm: "Fill 1",
              mn: "ADBE Vector Graphic - Fill",
              hd: false,
            },
            {
              ty: "tr",
              p: { a: 0, k: [532.5, 540], ix: 2 },
              a: { a: 0, k: [0, 0], ix: 1 },
              s: { a: 0, k: [100, 100], ix: 3 },
              r: { a: 0, k: 0, ix: 6 },
              o: { a: 0, k: 100, ix: 7 },
              sk: { a: 0, k: 0, ix: 4 },
              sa: { a: 0, k: 0, ix: 5 },
              nm: "Transform",
            },
          ],
          nm: "Group 1",
          np: 2,
          cix: 2,
          bm: 0,
          ix: 1,
          mn: "ADBE Vector Group",
          hd: false,
        },
      ],
      ip: 0,
      op: 121.001102762476,
      st: 0,
      bm: 0,
    },
    {
      ddd: 0,
      ind: 4,
      ty: 0,
      nm: "dots",
      refId: "comp_0",
      sr: 1,
      ks: {
        o: { a: 0, k: 100, ix: 11 },
        r: { a: 0, k: 67.5, ix: 10 },
        p: { a: 0, k: [540, 540, 0], ix: 2 },
        a: { a: 0, k: [540, 540, 0], ix: 1 },
        s: { a: 0, k: [80, 80, 100], ix: 6 },
      },
      ao: 0,
      w: 1080,
      h: 1080,
      ip: 3.00002734121841,
      op: 124.001130103694,
      st: 3.00002734121841,
      bm: 0,
    },
    {
      ddd: 0,
      ind: 5,
      ty: 0,
      nm: "dots",
      refId: "comp_0",
      sr: 1,
      ks: {
        o: { a: 0, k: 100, ix: 11 },
        r: { a: 0, k: 22.5, ix: 10 },
        p: { a: 0, k: [540, 540, 0], ix: 2 },
        a: { a: 0, k: [540, 540, 0], ix: 1 },
        s: { a: 0, k: [80, 80, 100], ix: 6 },
      },
      ao: 0,
      w: 1080,
      h: 1080,
      ip: 0,
      op: 121.001102762476,
      st: 0,
      bm: 0,
    },
    {
      ddd: 0,
      ind: 6,
      ty: 0,
      nm: "dots",
      refId: "comp_0",
      sr: 1,
      ks: {
        o: { a: 0, k: 100, ix: 11 },
        r: { a: 0, k: 0, ix: 10 },
        p: { a: 0, k: [540, 540, 0], ix: 2 },
        a: { a: 0, k: [540, 540, 0], ix: 1 },
        s: { a: 0, k: [100, 100, 100], ix: 6 },
      },
      ao: 0,
      w: 1080,
      h: 1080,
      ip: -7.00006379617628,
      op: 114.001038966299,
      st: -7.00006379617628,
      bm: 0,
    },
    {
      ddd: 0,
      ind: 7,
      ty: 0,
      nm: "dots",
      refId: "comp_0",
      sr: 1,
      ks: {
        o: { a: 0, k: 100, ix: 11 },
        r: { a: 0, k: 45, ix: 10 },
        p: { a: 0, k: [540, 540, 0], ix: 2 },
        a: { a: 0, k: [540, 540, 0], ix: 1 },
        s: { a: 0, k: [100, 100, 100], ix: 6 },
      },
      ao: 0,
      w: 1080,
      h: 1080,
      ip: -4.00003645495787,
      op: 117.001066307518,
      st: -4.00003645495787,
      bm: 0,
    },
  ],
  markers: [],
};

const INDUSTRIES = [
  "Information Technology",
  "Healthcare",
  "Finance & Banking",
  "Education",
  "Manufacturing",
  "Retail & E-commerce",
  "Sales & Marketing",
  "Automotive Industry",
  "Other",
];
const DESIGNATIONS = [
  "HR Manager",
  "Talent Acquisition Specialist",
  "Recruiter",
  "HR Director",
  "Hiring Manager",
  "Founder / CEO",
  "CTO / Tech Lead",
  "Sales Manager",
  "Freelancer",
  "Other",
];
const PERKS = [
  "Live AI interview demonstration",
  "Custom question generation for your roles",
  "Organization & recruiter setup walkthrough",
  "Result dashboard & analytics preview",
  "Automated candidate reports and insights overview",
];
const INIT = {
  first_name: "",
  last_name: "",
  email: "",
  countryCode: "+91",
  phone: "",
  company: "",
  industry: "",
  designation: "",
};

export default function Demo() {
  const theme = useTheme();
  const [form, setForm] = useState(INIT);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });

  // Frontend OTP workflow lifecycle states
  const [otpRequested, setOtpRequested] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpSending, setOtpSending] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0); // seconds remaining before resend is allowed
  const [lastFailedOtp, setLastFailedOtp] = useState(""); // last OTP value that failed -- locks Verify until it changes
  const otpRequestInFlight = useRef(false);
  const otpVerifyInFlight = useRef(false);

  const ref = useRef(null);

  useScrollReveal(ref, { stagger: 120 });

  // Tick the OTP resend cooldown down once a second while active.
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setInterval(() => {
      setResendCooldown((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  const validate = () => {
    const e = {};
    try {
      requestDemoSchema.validateSync(form, { abortEarly: false });
    } catch (err) {
      if (err.inner) {
        err.inner.forEach((validationError) => {
          if (!e[validationError.path]) {
            e[validationError.path] = validationError.message;
          }
        });
      }
    }

    // Custom lock: phone digit count based on country code
    if (!e.phone) {
      const selected = COUNTRY_CODES.find((c) => c.code === form.countryCode);
      const requiredDigits = selected?.digits || 10;
      if (form.phone && form.phone.length !== requiredDigits) {
        e.phone = `Phone number must be exactly ${requiredDigits} digits for ${form.countryCode}`;
      }
    }

    // OTP must be verified before submitting
    if (otpRequested && !otpVerified) {
      e.email = "Please verify your email with the OTP before submitting";
    }
    if (!otpVerified) {
      e.email = e.email || "Email must be verified before submitting";
    }

    if (!captchaToken) {
      e.captcha = "Please verify that you are not a robot";
    }

    return e;
  };

  // Button is enabled only when: all fields filled, email OTP verified, captcha done
  const isFormReady = useMemo(() => {
    return (
      form.first_name.trim() &&
      form.last_name.trim() &&
      form.email.trim() &&
      form.company.trim() &&
      form.phone.trim() &&
      form.industry &&
      form.designation &&
      otpVerified &&
      !!captchaToken
    );
  }, [form, otpVerified, captchaToken]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    let newValue = value;

    // Sanitize specific fields
    if (name === "first_name" || name === "last_name") {
      newValue = value.replace(/[^A-Za-z\s'-]/g, "");
    }
    if (name === "company") {
      newValue = value.replace(/[^A-Za-z0-9 ]/g, "");
    }

    // Update form value
    setForm((prev) => ({ ...prev, [name]: newValue }));

    // Live per-field validation using yup schema
    if (newValue === "" || newValue === undefined) {
      // Clear error when field is cleared
      setErrors((prev) => ({ ...prev, [name]: "" }));
      return;
    }

    try {
      // Validate only the touched field
      requestDemoSchema.validateSyncAt(name, { [name]: newValue });
      setErrors((prev) => ({ ...prev, [name]: "" }));
    } catch (err) {
      setErrors((prev) => ({ ...prev, [name]: err.message }));
    }
  };

  const handleRequestOtp = async () => {
    // Synchronous guard: blocks rapid repeat clicks fired before React has
    // re-rendered the disabled button (state updates are async).
    if (otpRequestInFlight.current) return;

    const trimmedEmail = form.email.trim();
    if (!trimmedEmail || !/\S+@\S+\.\S+/.test(trimmedEmail)) {
      setErrors((prev) => ({ ...prev, email: "Valid work email required" }));
      return;
    }

    otpRequestInFlight.current = true;
    setOtpSending(true);
    try {
      const res = await landingApis.requestDemoOTP({ email: trimmedEmail });
      const { status, data } = res || {};
      // Backend format: { status, code, message, data: { ... } }
      const message = data?.message;

      if (status === 200 || status === 201) {
        setOtpRequested(true);
        setResendCooldown(RESEND_COOLDOWN_SECONDS);
        setOtpCode("");
        setLastFailedOtp("");
        toast.success(message || "OTP sent to your email", { id: "demo-otp-send" });
        return;
      }

      if (status === 429) {
        setResendCooldown(RESEND_COOLDOWN_SECONDS);
        toast.error(message || "Too many OTP requests. Please try again later.", { id: "demo-otp-send" });
        return;
      }

      toast.error(message || "Failed to send OTP", { id: "demo-otp-send" });
    } catch (err) {
      // Axios throws for non-2xx — extract message from backend response body
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Something went wrong while sending the OTP";
      toast.error(message, { id: "demo-otp-send" });
    } finally {
      setOtpSending(false);
      otpRequestInFlight.current = false;
    }
  };

  const handleVerifyOtp = async () => {
    if (otpCode.trim().length !== 6) return;
    if (otpVerifyInFlight.current) return;

    otpVerifyInFlight.current = true;
    setOtpVerifying(true);
    try {
      const res = await landingApis.verifyDemoOTP({
        email: form.email.trim(),
        otp: otpCode.trim(),
      });
      const { status, data } = res || {};
      // Backend format: { status, code, message, data: { error_code?, ... } }
      const message = data?.message;

      if (status === 200 || status === 201) {
        setOtpVerified(true);
        setLastFailedOtp("");
        setErrors((prev) => ({ ...prev, email: "" }));
        toast.success(message || "Email verified successfully!", { id: "demo-otp-verify" });
        return;
      }

      setLastFailedOtp(otpCode.trim());
      toast.error(message || "Invalid or expired OTP", { id: "demo-otp-verify" });
    } catch (err) {
      setLastFailedOtp(otpCode.trim());
      // Axios throws for non-2xx (e.g. 401 Invalid OTP) — extract from response body
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Something went wrong while verifying the OTP";
      toast.error(message, { id: "demo-otp-verify" });
    } finally {
      setOtpVerifying(false);
      otpVerifyInFlight.current = false;
    }
  };

  const handleSubmit = async () => {
    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }

    setLoading(true);

    try {
      const res = await landingApis.requestDemo(form);

      const { status, data } = res || {};
      const message = data?.message;

      if (status >= 500) {
        setSnackbar({ open: true, message: message || "Server error. Please try again later.", severity: "error" });
        return;
      }

      if (status >= 400) {
        if (status === 409) {
          setSnackbar({ open: true, message: message || "Your demo request is already registered.", severity: "warning" });
          return;
        }
        setSnackbar({ open: true, message: message || "Request failed. Please check your details.", severity: "error" });
        return;
      }

      if (status === 200 || status === 201) {
        setSuccess(true);
        setSnackbar({ open: true, message: message || "Request submitted successfully!", severity: "success" });
        setForm(INIT)
        return;
      }

      setSnackbar({ open: true, message: message || "Unexpected response from the server.", severity: "warning" });
    } catch (err) {
      const message = err?.response?.data?.message || err?.message || "Something went wrong. Please try again.";
      setSnackbar({ open: true, message, severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  // MUI styles matching Demo.css form field look (glassmorphism dark inputs)
  const demoInputSx = {
    mb: "14px",
    "& .MuiInputLabel-root": { display: "none" },
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
      background: "rgba(255,255,255,0.05)",
      fontFamily: "'DM Sans', sans-serif",
      fontSize: "0.9rem",
      color: "#fff",
      "& fieldset": {
        border: "1.5px solid rgba(255,255,255,0.1)",
      },
      "&:hover fieldset": {
        borderColor: "rgba(255,255,255,0.25)",
      },
      "&.Mui-focused fieldset": {
        border: "1.5px solid #818cf8",
        boxShadow: "0 0 0 3px rgba(129,140,248,0.15)",
      },
      "& input": {
        padding: "12px 16px",
        color: "#fff",
        "-webkit-text-fill-color": "#fff",
        fontFamily: "'DM Sans', sans-serif",
        fontSize: "0.9rem",
        fontWeight: 400,
        // "&::placeholder": { color: "rgba(255,255,255,0.2)", opacity: 1, fontWeight: 200 },
        "&:disabled": {
          opacity: 0.6,
          background: "rgba(255,255,255,0.02)",
          cursor: "not-allowed",
          "-webkit-text-fill-color": "rgba(255,255,255,0.4)",
        },
      },
      "& .MuiSelect-select": {
        padding: "12px 16px",
        color: "#fff",
        "-webkit-text-fill-color": "#fff",
        fontFamily: "'DM Sans', sans-serif",
        fontSize: "0.9rem",
      },
      "& .MuiSvgIcon-root": { color: "rgba(255,255,255,0.5)" },
      "& .MuiInputAdornment-root": {
        color: "#fff",
      },
      "& .MuiInputAdornment-root select": {
        color: "#fff",
        fontFamily: "'DM Sans', sans-serif",
        fontSize: "0.9rem",
      },
    },
    "& .MuiFormHelperText-root": {
      color: "#f87171",
      marginLeft: 0,
      fontSize: "0.75rem",
    },
  };

  // Phone-specific sx: removes left input padding since startAdornment is present
  const demoPhoneInputSx = {
    ...demoInputSx,
    "& .MuiOutlinedInput-root": {
      ...demoInputSx["& .MuiOutlinedInput-root"],
      "& input": {
        ...demoInputSx["& .MuiOutlinedInput-root"]["& input"],
        paddingLeft: "6px",
      },
    },
  };

  return (
    <>
      <section id="demo" className="section-wrap" ref={ref}>
        <div className="demo-wrap reveal">
          <div className="demo-inner">
            {/* Left info panel */}
            <div className="demo-left">
              <div>
                <h2 className="demo-title">
                  Experience smarter hiring with the MeruBot
                </h2>
                <p className="demo-desc">
                  Get a personalized walkthrough, tailored to your industry, team
                  size, and hiring goals.
                </p>
                <ul className="demo-perks">
                  {PERKS.map((p) => (
                    <li key={p}>
                      <span className="perk-check">✓</span>
                      {p}
                    </li>
                  ))}
                </ul>
              </div>
              <p className="demo-footnote">
                We typically respond within 1 business day.
              </p>
            </div>

            {/* Right form panel */}
            <div className="demo-right" id="demo-form">
              {!success ? (
                <>
                  <h3>Book Your Demo</h3>
                  <p className="form-intro">
                    Fill in a few details and we'll be in touch shortly.
                  </p>
                  <div className="form-row">
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ fontSize: "0.8rem", fontWeight: 600, letterSpacing: "0.02em", mb: "5px", color: "inherit" }}>
                        First Name <Box component="span" sx={{ color: "#f87171" }}>*</Box>
                      </Box>
                      <CustomInput
                        required
                        name="first_name"
                        placeholder="Arjun"
                        inputProps={{ maxLength: 50 }}
                        value={form.first_name}
                        onChange={handleChange}
                        error={!!errors.first_name}
                        helperText={errors.first_name}
                        size="small"
                        sx={demoInputSx}
                      />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ fontSize: "0.8rem", fontWeight: 600, letterSpacing: "0.02em", mb: "5px", color: "inherit" }}>
                        Last Name <Box component="span" sx={{ color: "#f87171" }}>*</Box>
                      </Box>
                      <CustomInput
                        required
                        name="last_name"
                        placeholder="Mehta"
                        inputProps={{ maxLength: 50 }}
                        value={form.last_name}
                        onChange={handleChange}
                        error={!!errors.last_name}
                        helperText={errors.last_name}
                        size="small"
                        sx={demoInputSx}
                      />
                    </Box>
                  </div>
                  <div>

                    {/* Inline Email Verification Block */}
                    <div className="form-group">
                      <label>Work Email <span style={{ color: "red" }}>*</span></label>
                      <div className="email-input-row">
                        <input
                          type="email"
                          name="email"
                          placeholder="arjun@company.com"
                          maxLength={50}
                          value={form.email}
                          onChange={handleChange}
                          disabled={otpVerified}
                          className={errors.email ? "input-error" : ""}
                        />
                        {form.email.trim() && !otpRequested && !otpVerified && (
                          <button
                            type="button"
                            className="otp-action-btn"
                            disabled={otpSending || !!errors.email}
                            onClick={handleRequestOtp}
                          >
                            {otpSending ? "Sending…" : "Request OTP"}
                          </button>
                        )}
                      </div>
                      {errors.email && <span className="error-msg">{errors.email}</span>}
                    </div>

                    {/* Contextual OTP Input field layout display */}
                    {otpRequested && (
                      <div className="form-group slide-down-animation">
                        <label>Enter OTP <span style={{ color: "red" }}>*</span></label>
                        <div className="otp-input-row">
                          <input
                            type="text"
                            placeholder="123456"
                            maxLength={6}
                            value={otpCode}
                            onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ""))}
                            disabled={otpVerified}
                            className={`otp-small-input ${!otpCode && errors.email ? "input-error" : ""}`}
                          />
                          {!otpVerified ? (
                            <button
                              type="button"
                              className="otp-action-btn"
                              disabled={
                                otpCode.trim().length !== 6 ||
                                otpVerifying ||
                                (!!lastFailedOtp && otpCode.trim() === lastFailedOtp)
                              }
                              onClick={handleVerifyOtp}
                            >
                              {otpVerifying ? "Verifying…" : "Verify"}
                            </button>
                          ) : (
                            <span className="otp-verified-badge">
                              ✅ <span className="verified-text">Verified</span>
                            </span>
                          )}
                        </div>
                        {!otpVerified && (
                          <button
                            type="button"
                            className="otp-resend-btn"
                            disabled={resendCooldown > 0 || otpSending}
                            onClick={handleRequestOtp}
                          >
                            {otpSending
                              ? "Sending…"
                              : resendCooldown > 0
                                ? `Resend OTP in ${resendCooldown}s`
                                : "Resend OTP"}
                          </button>
                        )}
                      </div>
                    )}

                    <Box sx={{ mb: "14px" }}>
                      <Box sx={{ fontSize: "0.8rem", fontWeight: 600, letterSpacing: "0.02em", mb: "5px", color: "#fff" }}>
                        Contact number <Box component="span" sx={{ color: "#f87171" }}>*</Box>
                      </Box>
                      <CustomInput
                        required
                        name="phone"
                        placeholder={COUNTRY_CODES.find((c) => c.code === form.countryCode)?.digits === 9 ? "987654321" : "7788992211"}
                        type="tel"
                        size="small"
                        value={form.phone}
                        onChange={(e) => {
                          const numericValue = e.target.value.replace(/\D/g, "");
                          handleChange({ target: { name: "phone", value: numericValue } });
                        }}
                        inputProps={{
                          maxLength: COUNTRY_CODES.find((c) => c.code === form.countryCode)?.digits || 10,
                          inputMode: "numeric",
                          pattern: "[0-9]*",
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start" sx={{ mr: 0 }}>
                              <select
                                name="countryCode"
                                value={form.countryCode}
                                onChange={handleChange}
                                style={{
                                  border: "none",
                                  background: "transparent",
                                  fontSize: "0.9rem",
                                  cursor: "pointer",
                                  outline: "none",
                                  color: "#fff",
                                  fontFamily: "'DM Sans', sans-serif",
                                }}
                              >
                                {COUNTRY_CODES.map((c) => (
                                  <option key={c.code} value={c.code} style={{ background: "#0f0f1e", color: "#fff" }}>
                                    {c.label}
                                  </option>
                                ))}
                              </select>
                            </InputAdornment>
                          ),
                        }}
                        error={!!errors.phone}
                        helperText={errors.phone}
                        sx={demoPhoneInputSx}
                      />
                    </Box>
                  </div>
                  <Box>
                    <Box sx={{ fontSize: "0.8rem", fontWeight: 600, letterSpacing: "0.02em", mb: "5px", color: "inherit" }}>
                      Company / Organization <Box component="span" sx={{ color: "#f87171" }}>*</Box>
                    </Box>
                    <CustomInput
                      required
                      name="company"
                      placeholder="TechCorp India"
                      inputProps={{ maxLength: 50 }}
                      size="small"
                      value={form.company}
                      onChange={handleChange}
                      error={!!errors.company}
                      helperText={errors.company}
                      sx={demoInputSx}
                    />

                  </Box>
                  <div className="form-row">
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ fontSize: "0.8rem", fontWeight: 600, letterSpacing: "0.02em", mb: "5px", color: "inherit" }}>
                        Industry <Box component="span" sx={{ color: "#f87171" }}>*</Box>
                      </Box>
                      <CustomInput
                        select
                        required
                        name="industry"
                        placeholder="Select Industry"
                        value={form.industry}
                        onChange={handleChange}
                        size="small"
                        error={!!errors.industry}
                        helperText={errors.industry}
                        sx={{
                          ...demoInputSx,
                          "& .MuiMenu-paper": { background: "#0f0f1e" },
                        }}
                      >
                        {INDUSTRIES.map((i) => (
                          <MenuItem key={i} value={i} sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: "0.9rem" }}>{i}</MenuItem>
                        ))}
                      </CustomInput>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ fontSize: "0.8rem", fontWeight: 600, letterSpacing: "0.02em", mb: "5px", color: "inherit" }}>
                        Designation <Box component="span" sx={{ color: "#f87171" }}>*</Box>
                      </Box>
                      <CustomInput
                        select
                        required
                        name="designation"
                        placeholder="Select Designation"
                        value={form.designation}
                        onChange={handleChange}
                        size="small"
                        error={!!errors.designation}
                        helperText={errors.designation}
                        sx={{
                          ...demoInputSx,
                          "& .MuiMenu-paper": { background: "#0f0f1e" },
                        }}
                      >
                        {DESIGNATIONS.map((d) => (
                          <MenuItem key={d} value={d} sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: "0.9rem" }}>{d}</MenuItem>
                        ))}
                      </CustomInput>
                    </Box>
                  </div>

                  <div className="form-group" style={{ marginBottom: "1rem", marginTop: "16px" }}>
                    <div style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                    }}>
                      <div style={{
                        transform: "scale(0.78)",
                        transformOrigin: "center top",
                        marginBottom: "-14px",
                      }}>
                        <ReCAPTCHA
                          sitekey={
                            process.env.REACT_APP_RECAPTCHA_SITE_KEY ||
                            "your_placeholder_site_key_here"
                          }
                          onChange={(token) => {
                            setCaptchaToken(token);
                            if (errors.captcha)
                              setErrors((prev) => ({ ...prev, captcha: "" }));
                          }}
                        />
                      </div>
                    </div>
                    {errors.captcha && (
                      <span className="error-msg" style={{ display: "block", textAlign: "center" }}>{errors.captcha}</span>
                    )}
                  </div>

                  <button
                    className="submit-btn"
                    onClick={handleSubmit}
                    disabled={loading || !isFormReady}
                  >
                    {loading ? "Submitting…" : "Request Demo"}
                  </button>
                  <p className="form-consent">
                    <Typography
                      sx={{
                        mt: 2,
                        textAlign: "center",
                        fontSize: "0.7rem",
                        color: "#fff",
                        lineHeight: 1.5,
                      }}
                    >
                      By clicking Request Demo, you agree to our{" "}
                      <Link
                        href="privacy-policy?tab=terms&section=tos-definitions"
                        sx={{
                          color: "inherit",
                          fontWeight: 500,
                          textDecoration: "none",
                        }}
                      >
                        Terms of Service,
                      </Link>{" "}
                      <Link
                        href="privacy-policy?tab=ai&section=ai-definitions"
                        sx={{
                          color: "inherit",
                          fontWeight: 600,
                          textDecoration: "none",
                        }}
                      >
                        AI Features Terms & Conditions
                      </Link>{" "}
                      and{" "}
                      <Link
                        href="privacy-policy?tab=privacy&section=scope"
                        sx={{
                          color: "inherit",
                          fontWeight: 600,
                          textDecoration: "none",
                        }}
                      >
                        Privacy Policy
                      </Link>
                      .
                    </Typography>
                  </p>
                </>
              ) : (

                <div className="success-msg">
                  <div className="success-lottie">
                    <Lottie
                      animationData={tickAnimationData}
                      loop={false}
                      autoplay={true}
                      style={{ width: 260, height: 260 }}
                    />
                  </div>
                  <h2>You're on the list!</h2>
                  <p>
                    Thanks! Our team will reach out within 1 business day to
                    schedule your personalized demo.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
      <CustomSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      />
    </>
  );
}

function Field({
  label,
  name,
  type = "text",
  placeholder,
  value,
  onChange,
  error,
  maxLength,
}) {
  return (
    <div className="form-group">
      <label>{label}</label>
      <input
        type={type}
        name={name}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        maxLength={maxLength}
        className={error ? "input-error" : ""}
      />
      {error && <span className="error-msg">{error}</span>}
    </div>
  );
}